"use client";

import Link from "next/link";
import { useQuery } from "convex/react";
import { useAuth } from "@clerk/clerk-react";
import { api } from "../../convex/_generated/api";
import { Logo } from "@/components/brand/Logo";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { Skeleton } from "@/components/ui/skeleton";
import {
  StatsCard,
  FeatureCard,
  TrendingList,
  ActivityItem,
  statsIcons,
} from "@/components/home/HomeSections";
import {
  OrganizationsIcon,
  ArrowRightIcon,
  ChatIcon,
  SearchIcon,
} from "@/components/icons";
import { usePageTitle } from "@/lib/usePageTitle";
import { ParticleBackground } from "@/components/landing/ParticleBackground";
import { AnimatedHeroText } from "@/components/landing/AnimatedHeroText";

export default function Home() {
  usePageTitle("Home");
  const { isSignedIn: isAuthenticated, isLoaded } = useAuth();
  const authLoading = !isLoaded;
  const stats = useQuery(api.stats.getLiveStats);
  const recentActivity = useQuery(api.stats.getRecentActivity);
  const weeklyGrowthValue =
    stats === undefined
      ? undefined
      : stats.useWeeklyGrowthRate
        ? (stats.weeklyGrowthRate ?? 0)
        : stats.weeklyGrowthDelta;
  const weeklyGrowthDisplay =
    stats === undefined
      ? undefined
      : stats.useWeeklyGrowthRate
        ? `${(stats.weeklyGrowthRate ?? 0) > 0 ? "+" : ""}${(stats.weeklyGrowthRate ?? 0).toFixed(1)}%`
        : `${(stats.weeklyGrowthDelta ?? 0) > 0 ? "+" : ""}${stats.weeklyGrowthDelta ?? 0}`;
  const weeklyGrowthDescription =
    stats === undefined
      ? undefined
      : `${stats.currentWeekSignups ?? 0} signups this week vs ${stats.previousWeekSignups ?? 0} last week${stats.useWeeklyGrowthRate ? "" : " (low volume; showing net change)"}`;
  const weeklyGrowthHelpText = stats?.useWeeklyGrowthRate
    ? "Percentage change in user signups compared to the previous 7-day period."
    : "Net signup change versus last week. Percentage is hidden until last-week volume is high enough.";

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <Navbar />

      <main className="flex flex-1 flex-col">
        {/* Hero section — particle background + animated headline */}
        <section className="relative flex flex-1 flex-col items-center justify-center overflow-hidden px-4 py-16 text-center">
          {/* Particle canvas fills the hero section */}
          <ParticleBackground />

          {/* All hero content sits above the canvas */}
          <div className="relative z-10 mx-auto max-w-3xl">
            <div className="mb-6 flex justify-center">
              <Logo size="xl" className="text-primary" />
            </div>
            <h1 className="mb-4 text-4xl font-bold tracking-tight sm:text-5xl lg:text-6xl">
              Built for Hytale operators who need <AnimatedHeroText />
            </h1>
            <p className="mb-8 text-lg text-muted sm:text-xl">
              BlockyNetwork is live. It is the production operations layer for
              Hytale owners and staff: roles, permissions, and team visibility
              in one place.
            </p>
            <div className="flex flex-col items-center justify-center gap-4 sm:flex-row">
              {authLoading ? (
                <div className="h-12 w-48 animate-pulse rounded-md bg-muted" />
              ) : isAuthenticated ? (
                <Link
                  href="/dashboard"
                  className="inline-flex w-full items-center justify-center rounded-md bg-primary px-8 py-3 text-lg font-medium text-primary-foreground transition-colors hover:bg-primary/90 sm:w-auto"
                >
                  Go to Dashboard
                  <ArrowRightIcon className="ml-2 h-5 w-5" />
                </Link>
              ) : (
                <Link
                  href="/pricing"
                  className="inline-flex w-full items-center justify-center rounded-md bg-primary px-8 py-3 text-lg font-medium text-primary-foreground transition-colors hover:bg-primary/90 sm:w-auto"
                >
                  Start Free Trial
                  <ArrowRightIcon className="ml-2 h-5 w-5" />
                </Link>
              )}
              <Link
                href={isAuthenticated ? "/servers" : "/signup"}
                className="inline-flex w-full items-center justify-center rounded-md border border-border px-8 py-3 text-lg font-medium transition-colors hover:bg-foreground/5 sm:w-auto"
              >
                {isAuthenticated ? "Browse Servers" : "Create Account"}
              </Link>
            </div>
          </div>
        </section>

        <section className="border-t border-border bg-foreground/5 px-4 py-16">
          <div className="mx-auto max-w-6xl">
            <h2 className="mb-12 text-center text-3xl font-bold">
              Everything you need for launch-ready operations
            </h2>
            <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
              <FeatureCard
                icon={<OrganizationsIcon className="h-8 w-8" />}
                title="Launch Control Hub"
                description="Set up organizations, roles, and permissions with a structure designed to scale with your team."
              />
              <FeatureCard
                icon={<SearchIcon className="h-8 w-8" />}
                title="Team Roles"
                description="Assign owners, admins, and moderators with clear boundaries and full action visibility."
              />
              <FeatureCard
                icon={<ChatIcon className="h-8 w-8" />}
                title="Operational Visibility"
                description="Track member actions in real time, surface activity signals, and keep operations steady."
              />
            </div>
          </div>
        </section>

        <section className="px-4 py-16">
          <div className="mx-auto max-w-6xl">
            <h2 className="mb-8 text-center text-3xl font-bold">Live Platform Stats</h2>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <StatsCard
                icon={statsIcons.users}
                label="Total Players"
                value={stats?.totalUsers}
                helpText="Total registered players across the BlockyNetworks platform."
              />
              <StatsCard
                icon={statsIcons.organizations}
                label="Organizations"
                value={stats?.totalOrganizations}
                helpText="Total active server communities currently available on BlockyNetwork."
              />
              <StatsCard
                icon={statsIcons.online}
                label="Online Now"
                value={stats?.onlineNow}
                highlight
                helpText="Players currently online in connected game servers from fresh heartbeat updates."
              />
              <StatsCard
                icon={statsIcons.trending}
                label="Weekly Signup Growth"
                value={weeklyGrowthValue}
                displayValue={weeklyGrowthDisplay}
                description={weeklyGrowthDescription}
                helpText={weeklyGrowthHelpText}
              />
            </div>
          </div>
        </section>

        <section className="border-t border-border bg-foreground/5 px-4 py-16">
          <div className="mx-auto max-w-6xl">
            <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <h2 className="text-2xl font-bold">Trending Organizations</h2>
              <Link
                href="/servers"
                className="inline-flex items-center text-sm font-medium text-muted transition-colors hover:text-foreground"
              >
                View all
                <ArrowRightIcon className="ml-2 h-4 w-4" />
              </Link>
            </div>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {stats === undefined ? (
                <>
                  <Skeleton className="h-24" />
                  <Skeleton className="h-24" />
                  <Skeleton className="h-24" />
                </>
              ) : (
                <TrendingList items={stats.trending} />
              )}
            </div>
          </div>
        </section>

        <section className="px-4 py-16">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="mb-4 text-3xl font-bold">Recent Activity</h2>
            {recentActivity === undefined ? (
              <div className="space-y-3">
                <Skeleton className="h-12" />
                <Skeleton className="h-12" />
              </div>
            ) : recentActivity.length === 0 ? (
              <p className="text-muted">
                No recent activity. Join the community to see what&apos;s happening!
              </p>
            ) : (
              <div className="space-y-3 text-left">
                {recentActivity.slice(0, 10).map((activity) => (
                  <ActivityItem key={activity.id} activity={activity} />
                ))}
              </div>
            )}
          </div>
        </section>

        <section className="px-4 py-16">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="mb-4 text-3xl font-bold">
              Grow your server with confidence
            </h2>
            <p className="mb-8 text-muted">
              {isAuthenticated
                ? "Manage your organizations, streamline moderation workflows, and expand your operations with fewer surprises."
                : "Start your operations setup on BlockyNetwork and build a resilient team workflow from day one."}
            </p>
            <Link
              href={isAuthenticated ? "/dashboard" : "/pricing"}
              className="inline-flex items-center justify-center rounded-md bg-primary px-8 py-3 text-lg font-medium text-primary-foreground transition-colors hover:bg-primary/90"
            >
              {isAuthenticated ? "Go to Dashboard" : "View Plans & Subscribe"}
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
